import { useState, useRef, useCallback, useEffect } from 'react';
import { useVoiceRecognition } from './useVoiceRecognition';
import { useWhisperRecording } from './useWhisperRecording';

/**
 * useVoiceCapture — unified voice capture hook that picks the engine
 * (browser Web Speech API vs server-side OpenAI Whisper) based on the user's
 * `voice_engine` setting and supports both tap-to-record and long-press-tap-stop UX.
 *
 * Returned `pressHandlers` should be spread onto a button via:
 *   <button onPointerDown={...} onPointerUp={...} onPointerLeave={...} />
 *
 * Press behaviour:
 *   - Quick tap (<300ms hold)  → single-shot capture, auto-stops after a few seconds.
 *   - Long-press (≥300ms hold) → "tap-stop" mode: recording starts when the user
 *     releases, and continues until the user taps the button again.
 */
export function useVoiceCapture({ engine = 'webspeech', onResult, onError, prompt = '' } = {}) {
  const useWhisper = engine === 'whisper';
  const webSpeech = useVoiceRecognition({ onResult, onError });
  const whisper = useWhisperRecording({ onResult, onError, prompt });

  const active = useWhisper ? whisper : webSpeech;
  const isSupported = useWhisper ? whisper.isSupported : webSpeech.isSupported;
  const isListening = active.isListening;
  const isTranscribing = useWhisper ? whisper.isTranscribing : false;

  // Long-press detection
  const LONG_PRESS_MS = 300;
  const pressTimerRef = useRef(null);
  const longPressFiredRef = useRef(false);
  const [tapStopArmed, setTapStopArmed] = useState(false);

  useEffect(() => () => {
    if (pressTimerRef.current) clearTimeout(pressTimerRef.current);
  }, []);

  const startAutoCapture = useCallback(() => {
    // Brief, single-shot capture
    if (useWhisper) {
      whisper.startListening({ mode: 'auto', autoStopMs: 5000 });
    } else {
      webSpeech.startListening();
    }
  }, [useWhisper, whisper, webSpeech]);

  const startTapStopCapture = useCallback(() => {
    setTapStopArmed(true);
    if (useWhisper) {
      whisper.startListening({ mode: 'tap-stop' });
    } else {
      // Web Speech doesn't have a true tap-stop mode; stays listening until user taps stop
      webSpeech.startListening();
    }
  }, [useWhisper, whisper, webSpeech]);

  const stop = useCallback(() => {
    setTapStopArmed(false);
    if (useWhisper) {
      whisper.stopListening();
    } else {
      webSpeech.stopListening();
    }
  }, [useWhisper, whisper, webSpeech]);

  const handlePressDown = useCallback(() => {
    // If already recording in tap-stop mode, this press = STOP signal.
    if (tapStopArmed || isListening) {
      stop();
      return;
    }
    longPressFiredRef.current = false;
    pressTimerRef.current = setTimeout(() => {
      longPressFiredRef.current = true;
    }, LONG_PRESS_MS);
  }, [tapStopArmed, isListening, stop]);

  const handlePressUp = useCallback(() => {
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
    if (isListening || tapStopArmed) return; // we already handled stop on down
    if (longPressFiredRef.current) {
      startTapStopCapture();
    } else {
      startAutoCapture();
    }
    longPressFiredRef.current = false;
  }, [isListening, tapStopArmed, startAutoCapture, startTapStopCapture]);

  const handlePressCancel = useCallback(() => {
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
    longPressFiredRef.current = false;
  }, []);

  const pressHandlers = {
    onPointerDown: handlePressDown,
    onPointerUp: handlePressUp,
    onPointerCancel: handlePressCancel,
    onPointerLeave: handlePressCancel,
  };

  return {
    isSupported,
    isListening,
    isTranscribing,
    tapStopArmed,
    pressHandlers,
    startAutoCapture,
    startTapStopCapture,
    stop,
    engine: useWhisper ? 'whisper' : 'webspeech',
  };
}
